"use client";

import { useEffect, useRef, useState } from "react";
import styles from "./mobile-section-nav.module.css";

export interface SectionNavItem {
  /** Target element id on the page. */
  id: string;
  label: string;
  /** Font Awesome solid icon class, e.g. "fa-route". */
  icon: string;
}

/**
 * Mobile-only quick-jump bar pinned to the bottom of the viewport. On phones /
 * tablets the sidebar (Package Info, Map) stacks below the long main content,
 * so this lets the user jump straight to any section. It auto-hides while
 * scrolling down (to stay out of the way) and highlights the section in view.
 *
 * Pure-CSS sticky elsewhere on the page is untouched; this adds only a tiny
 * rAF-throttled scroll handler and one IntersectionObserver.
 */
export default function MobileSectionNav({
  items,
}: {
  items: SectionNavItem[];
}) {
  const [isMobile, setIsMobile] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(items[0]?.id ?? null);
  const [hidden, setHidden] = useState(false);
  const offsetRef = useRef(80);
  // While a tap-triggered scroll is in flight, don't let the downward motion
  // auto-hide the bar.
  const lockRef = useRef(false);
  const lockTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Only engage on stacked (mobile / tablet) layouts.
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 991px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!isMobile || items.length < 2) return;

    // Header-aware scroll offset (matches the site's sticky header height).
    const measure = () => {
      const h = document
        .querySelector(".sticky-wrapper")
        ?.getBoundingClientRect().height;
      offsetRef.current = (h && h > 0 ? h : 70) + 10;
    };
    measure();

    // Reserve space so the fixed bar never covers the last content / footer.
    document.body.style.paddingBottom =
      "calc(64px + env(safe-area-inset-bottom, 0px))";

    // Scroll-spy: highlight whichever section sits nearest the top.
    const els = items
      .map((i) => document.getElementById(i.id))
      .filter((e): e is HTMLElement => !!e);
    let io: IntersectionObserver | null = null;
    if (els.length && typeof IntersectionObserver !== "undefined") {
      const tops = new Map<string, number>();
      io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (e.isIntersecting) tops.set(e.target.id, e.boundingClientRect.top);
            else tops.delete(e.target.id);
          }
          const sorted = Array.from(tops.entries()).sort((a, b) => a[1] - b[1]);
          if (sorted.length) setActiveId(sorted[0][0]);
        },
        { rootMargin: `-${offsetRef.current}px 0px -62% 0px`, threshold: 0 },
      );
      els.forEach((el) => io!.observe(el));
    }

    // Auto-hide on scroll down, reveal on scroll up / near top / near bottom.
    let lastY = window.scrollY;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        const dy = y - lastY;
        lastY = y;
        ticking = false;
        if (lockRef.current) return; // tap-scroll in progress
        const nearBottom =
          window.innerHeight + y >=
          document.documentElement.scrollHeight - 90;
        if (nearBottom || y < 140) setHidden(false);
        else if (dy > 6) setHidden(true);
        else if (dy < -6) setHidden(false);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", measure);

    return () => {
      io?.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", measure);
      if (lockTimer.current) clearTimeout(lockTimer.current);
      document.body.style.paddingBottom = "";
    };
  }, [isMobile, items]);

  // Tag <body> so global CSS lifts the third-party chat launcher (fixed to the
  // bottom-right outside this component's DOM) clear of the bar — but ONLY
  // while the bar is actually on screen. When it auto-hides on scroll-down,
  // drop it back to normal so it doesn't float in empty space.
  useEffect(() => {
    const visible = isMobile && items.length >= 2 && !hidden;
    document.body.classList.toggle("has-mobile-section-nav", visible);
    return () => document.body.classList.remove("has-mobile-section-nav");
  }, [isMobile, items.length, hidden]);

  if (!isMobile || items.length < 2) return null;

  const go = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - offsetRef.current;
    lockRef.current = true;
    if (lockTimer.current) clearTimeout(lockTimer.current);
    lockTimer.current = setTimeout(() => {
      lockRef.current = false;
    }, 900);
    setHidden(false);
    window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
    setActiveId(id);
  };

  return (
    <nav
      className={`${styles.bar} ${hidden ? styles.hidden : ""}`}
      aria-label="Jump to section"
    >
      {items.map((it) => {
        const isActive = activeId === it.id;
        return (
          <button
            key={it.id}
            type="button"
            className={`${styles.item} ${isActive ? styles.active : ""}`}
            onClick={() => go(it.id)}
            aria-current={isActive ? "true" : undefined}
          >
            <i className={`fa-solid ${it.icon} ${styles.itemIcon}`} aria-hidden="true" />
            <span className={styles.itemLabel}>{it.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
